import { useAppDispatch } from 'client/hooks/use-dispatch'
import * as pageActions from 'client/store/page/pageActions'
import { FontProps } from '../components/Font/BaseFont'
import { BaseTextContentEntityInterface } from 'src/shared/types/base-entity-interface'

export function useUpdateEntity<T extends Record<string, any>>() {
  const dispatch = useAppDispatch()
  return function (updatedEntity: T) {
    dispatch(pageActions.updateEntity<T>(updatedEntity))
  }
}

export function useUpdateProp<T extends Record<string, any>>(entity: T) {
  const dispatch = useAppDispatch()
  return function <K extends keyof T>(prop: K) {
    return function (value: T[K]) {
      dispatch(
        pageActions.updateEntity<T>({
          ...entity,
          [prop]: value,
        }),
      )
    }
  }
}

export function useUpdateProps<T extends BaseTextContentEntityInterface>(
  entity: T,
) {
  const dispatch = useAppDispatch()
  return function (styles: FontProps) {
    dispatch(
      pageActions.updateEntity<T>({
        ...entity,
        titleFontFamily: styles.fontFamily,
        titleFontStyle: styles.fontStyle,
        titleFontWeight: styles.fontWeight,
      }),
    )
  }
}

/**
 * NOTE: It can be used for updating any nested prop, not only for the first level prop
 * NOTE: If the first level prop is optional, use the useUpdateNestedProp with the MakeRequired utility type:
 * ------ Example:-----
 * interface BlogPostListingInterface {
 *   postCard?: {
 *     backgroundColor: string
 *   }
 * }
 * const updateProp = useUpdateNestedProp(entity as MakeRequired<BlogPostListingInterface, 'postCard'>)
 * --------------------
 * The same is for the second level prop, if it is optional
 */
export const useUpdatePropNew = <T extends Record<string, any>>(obj: T) => {
  const dispatch = useAppDispatch()
  return <
    K1 extends keyof T,
    K2 extends keyof T[K1] | undefined,
    // @ts-ignore it works, but the typescript mark it as error
    K3 extends keyof T[K1][K2] | undefined,
    // @ts-ignore it works, but the typescript mark it as error
    K4 extends keyof T[K1][K2][K3] | undefined,
  >(
    firstLevelPropKey: K1,
    secondLevelPropKey?: K2,
    thirdLevelPropKey?: K3,
    fourthLevelPropKey?: K4,
  ) => {
    return (
      value: K2 extends undefined
        ? T[K1]
        : K3 extends undefined
          ? // @ts-ignore it works, but the typescript mark it as error
            T[K1][K2]
          : K4 extends undefined
            ? // @ts-ignore it works, but the typescript mark it as error
              T[K1][K2][K3]
            : // @ts-ignore it works, but the typescript mark it as error
              T[K1][K2][K3][K4],
    ) => {
      dispatch(
        pageActions.updateEntity<T>({
          ...obj,
          [firstLevelPropKey]: secondLevelPropKey
            ? thirdLevelPropKey
              ? fourthLevelPropKey
                ? {
                    ...obj[firstLevelPropKey],
                    [secondLevelPropKey]: {
                      ...obj[firstLevelPropKey][secondLevelPropKey],
                      [thirdLevelPropKey]: {
                        ...obj[firstLevelPropKey][secondLevelPropKey][
                          thirdLevelPropKey
                        ],
                        [fourthLevelPropKey]: value,
                      },
                    },
                  }
                : {
                    ...obj[firstLevelPropKey],
                    [secondLevelPropKey]: {
                      ...obj[firstLevelPropKey][secondLevelPropKey],
                      [thirdLevelPropKey]: value,
                    },
                  }
              : {
                  ...obj[firstLevelPropKey],
                  [secondLevelPropKey]: value,
                }
            : value,
        }),
      )
    }
  }
}
